import {
	IComponentOptions,
	IController,
	IControllerConstructor,
	IDocumentService,
	Injectable,
	ISCEDelegateService,
	ISCEService,
	IScope,
	IWindowService,
	material,
	module,
} from 'angular';

import { IAttachmentFile, IAttachmentsController } from './mx-attachment.interfaces';
import { I18n } from '../../../i18n/mx-internationalization';
import { MxAttachmentProcessService } from '../../PandoraSolutionBuilderAgent/modules/Attachment/mx-attachment-process.service';
import { OperationStatus, ProcessItem } from '../../Pandora/model/process-item';
import { EventService } from '../../../App/services/event-service';
import { EventCodes } from '../../../App/model/event-codes';
import { EventUnsubscribe } from '../../../Utils/mx-handler.collection';
import { HttpService } from '../../../App/services/http-service';
import { ConfigService } from '../../../App/services/mx-config-service';

class AttachmentPopupDialogController implements IController {
	public $mdDialog: material.IDialogService;
	static $inject = ['$mdDialog'];

	constructor($mdDialog: material.IDialogService) {
		this.$mdDialog = $mdDialog;
	}

	cancel() {
		this.$mdDialog.cancel();
	}
}

class AttachmentController implements IController {
	public file?: IAttachmentFile;
	public type?: string;
	public width?: string;
	public height?: string;
	public default?: string;
	public onSelectFile?: (options: { [file: string]: IAttachmentFile }) => void;
	private trustedUrl?: string;
	private _type = '';
	private enableSelection = false;
	private downloadLabel: string;
	private deleteLabel: string;
	private commentLabel: string;
	private cancelLabel: string;
	private i18n: { [key: string]: string };
	private i18nCommon: { [key: string]: string };
	private mxAttachments: IAttachmentsController;
	private process?: ProcessItem;

	static $inject = [
		'$document',
		'$window',
		'$sce',
		'$sceDelegate',
		'$mdDialog',
		'mx.internationalization',
		'mx.sb.Attachment.MxAttachmentProcessService',
		'mx.shell.EventService',
		'mx.SolutionBuilderAgent.Http',
		'mx.shell.Config',
		'$scope',
	];
	private size: string;
	private processEventUnsubscribes: EventUnsubscribe[];
	private disableExternalAttachmentsPreview: boolean;

	constructor(
		private readonly $document: IDocumentService,
		private readonly $window: IWindowService,
		private readonly $sce: ISCEService,
		private readonly $sceDelegate: ISCEDelegateService,
		private readonly $mdDialog: material.IDialogService,
		i18n: I18n,
		private readonly attachmentProcessService: MxAttachmentProcessService,
		private readonly eventService: EventService,
		private readonly http: HttpService,
		private readonly configService: ConfigService,
		private readonly $scope: IScope
	) {
		this.i18n = i18n.get('components.mx-attachments') as { [key: string]: string };
		this.i18nCommon = i18n.get('components.common') as { [key: string]: string };
		this.downloadLabel = this.i18n.download;
		this.deleteLabel = this.i18n.delete;
		this.cancelLabel = this.i18n.cancel;
		this.commentLabel = this.i18nCommon.comment;
		this.size = '';
		this.mxAttachments = <IAttachmentsController>{};
		this.processEventUnsubscribes = [];
		this.disableExternalAttachmentsPreview = this.configService.get('disableExternalAttachmentsPreview');
	}

	$onInit() {
		this.enableSelection = (this.mxAttachments.enableSelection || '').toLowerCase() === 'true';

		this._type = (this.file?.type || 'text').replace('application/', '').split('/').shift() || '';

		if (this.file?.size) {
			const sizeKb = Math.round(this.file.size / 1024);
			const sizeMb = Math.round(sizeKb / 1024);
			const sizeGb = Math.round(sizeMb / 1024);

			this.size = sizeGb > 1 ? sizeGb + ' G' : sizeMb > 1 ? sizeMb + ' M' : sizeKb > 1 ? sizeKb + ' K' : this.file.size + ' B';
		}

		const process = this.file?.id ? this.attachmentProcessService.getForegroundProcessById(this.file.id) : undefined;
		if (process) {
			this.bindProcessCallbacks(process);
		} else {
			this.eventService.subscribeTo(
				EventCodes.ForegroundProcessCreated,
				(event, process: ProcessItem) => {
					if (this.file && this.file.id && process.id === this.file.id) {
						this.bindProcessCallbacks(process);
					}
				},
				this.$scope
			);
		}
	}

	$onDestroy() {
		this.processEventUnsubscribes.forEach((unsubscribe) => unsubscribe());
	}

	private bindProcessCallbacks(process: ProcessItem): void {
		this.process = process;
		const onProgressUpdatedUnsubscribe = this.process.onProgressUpdated(async (progress) => await this.onUploadingProgress(progress));
		const onProcessStatusUpdatedUnsubscribe = this.process.onProcessStatusUpdated(async (status) => await this.onUploadStatusUpdate(status));
		const onProcessCancelingUnsubscribe = this.process.onProcessCanceling(async () => await this.onUploadingCanceling());
		const onProcessFailedUnsubscribe = this.process.onProcessFailed(async () => await this.onUploadingFailed());
		this.processEventUnsubscribes.push(...[], onProgressUpdatedUnsubscribe, onProcessStatusUpdatedUnsubscribe, onProcessCancelingUnsubscribe, onProcessFailedUnsubscribe);
	}

	public async onUploadingProgress(progress: number): Promise<void> {
		if (this.file) {
			this.file.comment = progress > 0 && progress < 100 ? `Uploading ${progress}%` : '';
		}
	}

	public async onUploadStatusUpdate(status: OperationStatus): Promise<void> {
		if (this.file) {
			switch (status) {
				case OperationStatus.Finished:
					this.file.comment = '';
					this.file.uploaded = true;
					break;
				case OperationStatus.Canceled:
					this.cancelUpload();
					break;
			}
		}
	}

	public async onUploadingCanceling(): Promise<boolean> {
		return Boolean(await this.mxAttachments.onUploadCanceling());
	}

	public async onUploadingFailed(): Promise<void> {
		await this.mxAttachments.loadFiles();
	}

	public async onCancelClick(): Promise<void> {
		await this.process?.cancel();
	}

	public async onImageClick() {
		const template = `<img ondragstart="return false;" src="${await this.getUrl()}" alt="${this.file?.name}" />`;
		this.showInDialog(template);
	}

	public async onVideoClick() {
		const url = await this.getUrl();
		if (url) {
			const template = `
				<video controls class="mx-attachment__video">
					<source ng-src="${url}" type="video/mp4">
					<source ng-src="${url}" type="video/ogg">
					<source ng-src="${url}" type="video/WebM">
					<span class="mx-attachment__video-warning">${this.i18n.videoWarning}</span>
				</video>`;

			this.showInDialog(template);
		}
	}

	private getFileExtension(): string {
		return (this.file?.name.includes('.') ? this.file?.name.split('.').at(-1) || '' : '').toLowerCase();
	}

	private isMsOfficeFile(): boolean {
		return ['doc', 'docx', 'xls', 'xlsx', 'pps', 'ppsx', 'ppt', 'pptx'].includes(this.getFileExtension());
	}

	public async onInlineFileClick() {
		if (!this.disableExternalAttachmentsPreview && this.file) {
			if (this.isMsOfficeFile()) {
				if (await this.canPreviewOfficeFile()) {
					this.$window.open(await this.getOfficePreviewUrl());
				} else {
					await this.downloadFile();
				}
			} else {
				this.$window.open(await this.getInlineUrl());
			}
		} else {
			await this.downloadFile();
		}
	}

	private async getInlineUrl(): Promise<string> {
		if (!this.file) {
			return '';
		}

		this.file.inline = true;
		const fileUrl = await this.getUrl();
		this.file.inline = false;
		return fileUrl;
	}

	private async getOfficePreviewUrl(): Promise<string> {
		return this.file?.isPublic && !this.file?.sendContentThroughServer
			? `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(await this.getInlineUrl())}`
			: await this.http.get<string>(`api/fileStorage/officeFilePreviewUrl/${this.file?.id}`);
	}

	private async canPreviewOfficeFile(): Promise<boolean> {
		return (this.file?.isPublic && !this.file?.sendContentThroughServer) || (await this.http.get<boolean>(`api/fileStorage/canPreviewOfficeFile/${this.file?.id}`));
	}

	private showInDialog(template: string) {
		this.$mdDialog.show({
			clickOutsideToClose: true,
			controllerAs: 'vm',
			controller: AttachmentPopupDialogController,
			template: `
				<md-dialog class="mx-attachment-popup">
					<md-dialog-content>
						<div layout="column" layout-align="end" class="attachment__dialog" ng-click="vm.cancel()">
							${template}
						</div>
					</md-dialog-content>
				</md-dialog>`,
		});
	}

	public isUploaded(): boolean {
		return this.file?.uploaded === true;
	}

	public isNewBox(): boolean {
		return this._type === 'new';
	}

	public isPdf(): boolean {
		return this._type === 'pdf';
	}

	public isImage(): boolean {
		return this._type === 'image';
	}

	public isGeneralFileType(): boolean {
		return !this.isImage() && !this.isPdf() && !this.isVideo() && !this.isNewBox();
	}

	public isVideo(): boolean {
		return this._type === 'video';
	}

	public showDownload(): boolean {
		return this.isUploaded();
	}

	public isEditMode(): boolean {
		return this.isUploaded() && !this.mxAttachments.readOnly;
	}

	private async getUrl() {
		if (this.file && !this.file.isVirtual && this.isUploaded()) {
			this.file.url = await this.mxAttachments.onGetFileUrl({ file: this.file });
		}
		const newUrl = this.file?.isVirtual ? this.file.url : this.mxAttachments.formatUrl({ url: this.file?.url || '' }) || this.file?.url;
		if (!this.trustedUrl || this.$sceDelegate.valueOf(this.trustedUrl) !== newUrl) {
			this.trustedUrl = this.$sce.trustAsResourceUrl(newUrl);
		}
		return this.trustedUrl || '';
	}

	public deleteFile() {
		this.mxAttachments.onDelete({ file: this.file });
	}

	private cancelUpload() {
		this.mxAttachments.onUploadCanceled({ file: this.file });
	}

	public editComment() {
		this.mxAttachments.onComment({ file: this.file });
	}

	public async downloadFile() {
		if (/^data:[^;]*;[^;]+,/.test(this.file?.url || '')) {
			const a = (this.$document[0] as Document).createElement('a');
			a.href = await this.getUrl();
			a.download = this.file?.name || 'dummy';
			a.click();
		} else {
			if (this.file) {
				this.mxAttachments.onDownloadFiles({ files: [this.file] });
			}
		}
	}
}

export class AttachmentComponent implements IComponentOptions {
	controller: Injectable<IControllerConstructor>;
	bindings: { [boundProperty: string]: string };

	controllerAs = 'vm';
	templateUrl = 'mx-attachments/mx-attachment.html';
	transclude = true;
	require: { [controller: string]: string } = { mxAttachments: '?^' };

	constructor() {
		this.bindings = {
			file: '=?',
			type: '@?',
			width: '@?',
			height: '@?',
			default: '@?',
			onSelectFile: '&',
		};

		this.controller = AttachmentController;
	}
}

module('mx.components').component('mxAttachment', new AttachmentComponent());
